import { TopBar } from 'screens/VirtualMarketerOnboarding/TopBar/TopBar'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './NextGenContentPreferences.module.scss'
import {
  ContentModificationRow,
  LABEL_MAP,
  MOBILE_DESCRIPTION_MAP,
} from 'screens/BrandKits/components/ContentModification/ContentModificationRow'
import { SourceImageEditLevel } from 'types'
import { useState } from 'react'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { Button } from 'blaze-ui/Button'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import cx from 'classnames'
import { ArrowLeft, MoreDots } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { SupportChat } from 'components/FirstTimeExperienceModal/components/SupportChat/SupportChat'
import { ThreeDotMenuDrawer } from 'screens/VirtualMarketerOnboarding/ThreeDotMenuDrawer/ThreeDotMenuDrawer'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboard } from 'hooks/queries/useOnboard'
import { useUpdateUserAppPreferences } from 'hooks/queries/useUpdateUserAppPreferences'
import { useSchedules } from 'hooks/queries/workspaces/schedules/useSchedules'
import dayjs from 'dayjs'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

export interface NextGenContentPreferencesProps {
  onNext: (sourceImageEditLevel: SourceImageEditLevel) => void
  onBack: () => void
  brandPlanId: number
  brandKitId: number
  initialVariant: SourceImageEditLevel
  campaignData?: {
    growth_goals_input: string
    key_dates_input: string
  }
  startDate?: string
  subscriptionPriceId?: string
  highlightStep?: string
  hideBack?: boolean
}

export const NextGenContentPreferences = observer(
  ({
    onNext,
    onBack,
    brandPlanId,
    brandKitId,
    initialVariant,
    campaignData,
    startDate,
    subscriptionPriceId,
    highlightStep = 'yourBrand',
    hideBack = false,
  }: NextGenContentPreferencesProps) => {
    const { drawersStore } = useRootStore()
    const { mutateAsync: updatePreferences } = useUpdateUserAppPreferences()
    const schedulesQuery = useSchedules({
      orderBy: 'start_date',
      orderDirection: 'asc',
      items: 15,
      startDate: dayjs().format('YYYY-MM-DD'),
    })
    const { mutateAsync: startBrandPlan } = useStartBrandPlan({ brandPlanId })
    const { mutateAsync: onboard } = useOnboard()
    const [variant, setVariant] = useState<SourceImageEditLevel>(initialVariant || 'high')
    const { isMobile } = useResponsiveDesign()
    const [isSaving, setIsSaving] = useState(false)
    const showNoContentModification = useFlipperFlag('show_no_content_modification')
    const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
    const { mutateAsync: updateBrandKit, isLoading: isUpdatingBrandKit } = useUpdateBrandKit({
      id: brandKitId,
    })

    const abortController = new AbortController()

    const exampleImages = [
      {
        id: 'high',
        src: 'https://res.cloudinary.com/almanac/image/upload/v1773422150/blaze_assets/virtual_marketer/onboarding/bb1_th3hrm.png',
        mobileSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1773748468/blaze_assets/virtual_marketer/onboarding/Frame_2147229169_mlm19r.png',
      },
      {
        id: 'medium',
        src: 'https://res.cloudinary.com/almanac/image/upload/v1773422151/blaze_assets/virtual_marketer/onboarding/bb2_b8iktj.png',
        mobileSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1773748468/blaze_assets/virtual_marketer/onboarding/Frame_21472291692_fvqizs.png',
      },
      {
        id: 'low',
        src: 'https://res.cloudinary.com/almanac/image/upload/v1773422150/blaze_assets/virtual_marketer/onboarding/bb3_men5xe.png',
        mobileSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1773748468/blaze_assets/virtual_marketer/onboarding/Frame_21472291693_dzppdv.png',
      },
      {
        id: 'none',
        src: 'https://res.cloudinary.com/almanac/image/upload/v1773422149/blaze_assets/virtual_marketer/onboarding/bb4_idksf4.png',
        mobileSrc:
          'https://res.cloudinary.com/almanac/image/upload/v1773748985/blaze_assets/virtual_marketer/onboarding/Frame_21472291694_std9tv.png',
      },
    ]

    const startGenerating = async () => {
      try {
        await startBrandPlan({
          growth_goals_input: campaignData?.growth_goals_input,
          key_dates_input: campaignData?.key_dates_input,
          start_date: startDate,
          subscription_price_id: subscriptionPriceId,
          signal: abortController.signal,
        })

        await schedulesQuery.refetch()

        await onboard()
        await updatePreferences({ scheduledPostsView: 'calendar' }) // Make sure user lands on calendar view
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return
        }
        showErrorMessageToast('Failed to generate campaigns. Please try again.')
        onBack()
      }
    }

    const handleNext = async () => {
      setIsSaving(true)
      await updateBrandKit({
        content_preferences: {
          ...brandKitData?.brand_kit?.content_preferences,
          source_image_edit_level: variant,
        },
      })

      if (isMobile) {
        await startGenerating()
      }

      setIsSaving(false)
      onNext(variant)
    }

    const openLogoutDrawer = () => {
      drawersStore.openDrawer(ThreeDotMenuDrawer)
    }

    const handleVariantChange = (variant: SourceImageEditLevel) => {
      if (isSaving) return

      setVariant(variant)
    }

    const selectedImage = exampleImages.find((image) => image.id === variant)

    if (isMobile) {
      return (
        <div className={Styles.mobileRoot}>
          <div className={cx(Styles.mobileTopBar, { [Styles.isWhite]: selectedImage?.id === 'high' })}>
            <IconButton variant="tertiary" icon={() => <ArrowLeft size={24} />} onPress={onBack} />
            <div className={Styles.threeDotMenuContainer}>
              <IconButton variant="tertiary" onPress={openLogoutDrawer} icon={MoreDots} color="var(--light-100)" />
              <SupportChat mobile />
            </div>
          </div>
          <div className={Styles.mobileContent}>
            <img src={selectedImage?.mobileSrc} alt={variant} className={Styles.selectedImage} />
            <div className={Styles.mobileContentInner}>
              <Heading level={2}>Choose how much Blaze should enhance your content.</Heading>
              <div className={Styles.mobilePreferences}>
                {['high', 'medium', 'low', ...(showNoContentModification ? ['none'] : [])].map((preferenceVariant) => (
                  <div
                    className={cx(Styles.mobilePreferenceOption, { [Styles.selected]: preferenceVariant === variant })}
                    key={preferenceVariant}
                    onClick={() => handleVariantChange(preferenceVariant as SourceImageEditLevel)}
                  >
                    <Heading level={5}>{LABEL_MAP[preferenceVariant as SourceImageEditLevel]}</Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      {MOBILE_DESCRIPTION_MAP[preferenceVariant as SourceImageEditLevel]}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
            <div className={Styles.mobileFooter}>
              <Button size="lg" onPress={handleNext} isDisabled={isSaving || isUpdatingBrandKit} fullWidth>
                {isSaving ? (
                  <>
                    Generating content
                    <LoadingEllipses />
                  </>
                ) : (
                  'Generate my content'
                )}
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className={Styles.root}>
        <TopBar highlightStep={highlightStep as React.ComponentProps<typeof TopBar>['highlightStep']} />
        <div className={Styles.leftSide}>
          <div className={Styles.content}>
            <div className={Styles.title}>
              <Heading level={1}>How much should Blaze enhance your content?</Heading>
              <div className={Styles.description}>
                <Text color="var(--dark-60)">
                  More creative freedom means better performance, or stay closer to your originals.
                </Text>
              </div>
            </div>
            <div className={Styles.preferences}>
              <ContentModificationRow selected={variant === 'high'} variant="high" onSelect={setVariant} />
              <ContentModificationRow selected={variant === 'medium'} variant="medium" onSelect={setVariant} />
              <ContentModificationRow selected={variant === 'low'} variant="low" onSelect={setVariant} />
              {showNoContentModification && (
                <ContentModificationRow selected={variant === 'none'} variant="none" onSelect={setVariant} />
              )}
            </div>
          </div>
          <div className={cx(Styles.footer, { [Styles.footerEnd]: hideBack })}>
            {!hideBack && (
              <Button variant="tertiary" size="lg" onPress={onBack} isDisabled={isSaving}>
                Back
              </Button>
            )}
            <Button size="lg" onPress={handleNext} isDisabled={isSaving || isUpdatingBrandKit}>
              {isSaving ? (
                <>
                  Saving
                  <LoadingEllipses />
                </>
              ) : (
                'Generate my content'
              )}
            </Button>
          </div>
        </div>
        <img src={selectedImage?.src} alt={variant} className={Styles.image} />
      </div>
    )
  },
)
